import { useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Eye, Pencil, Trash2 } from "lucide-react";
import { DataColumnHeader } from "@/components/molecules/table";
import {
  SelfContainedDataTable,
  selectedIncludesFn,
} from "@/components/organisms/SelfContainedDataTable";
import type { Minister } from "@/models/minister";

type StatusFilter = "active" | "inactive" | "all";

interface MinistersTableProps {
  ministers: Minister[];
  isLoading: boolean;
  canEdit: boolean;
  onView: (minister: Minister) => void;
  onEdit: (minister: Minister) => void;
  onDelete: (minister: Minister) => void;
  hideDistrictColumn?: boolean;
  hideRegionColumn?: boolean;
}

export function MinistersTable({
  ministers,
  isLoading,
  canEdit,
  onView,
  onEdit,
  onDelete,
  hideDistrictColumn = false,
  hideRegionColumn = false,
}: MinistersTableProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");

  // Pre-filter by status before the table sees the data so faceted counts
  // reflect only the active/inactive subset.
  const statusFiltered = useMemo(() => {
    if (statusFilter === "all") return ministers;
    const active = statusFilter === "active";
    return ministers.filter((m) => m.is_active === active);
  }, [ministers, statusFilter]);

  const columns = useMemo<ColumnDef<Minister>[]>(
    () => [
      {
        accessorKey: "adb_code",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Registro ADB" />
        ),
        filterFn: selectedIncludesFn,
      },
      {
        accessorKey: "full_name",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Nombre Completo" />
        ),
        filterFn: selectedIncludesFn,
      },
      {
        accessorKey: "dni",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="CI" />
        ),
        filterFn: selectedIncludesFn,
      },
      {
        accessorKey: "degree.name",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Grado Ministerial" />
        ),
        filterFn: selectedIncludesFn,
      },
      ...(!hideRegionColumn
        ? [
            {
              accessorKey: "region.name",
              header: ({ column }: { column: Parameters<typeof DataColumnHeader>[0]["column"] }) => (
                <DataColumnHeader column={column} title="Región" />
              ),
              filterFn: selectedIncludesFn,
            } as ColumnDef<Minister>,
          ]
        : []),
      ...(!hideDistrictColumn
        ? [
            {
              accessorKey: "district.name",
              header: ({ column }: { column: Parameters<typeof DataColumnHeader>[0]["column"] }) => (
                <DataColumnHeader column={column} title="Distrito" />
              ),
              filterFn: selectedIncludesFn,
            } as ColumnDef<Minister>,
          ]
        : []),
      {
        id: "actions",
        header: "Acciones",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const minister = row.original;
          return (
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); onView(minister); }}
                title="Ver detalles"
              >
                <Eye className="size-4" />
              </Button>
              {canEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => { e.stopPropagation(); onEdit(minister); }}
                  title="Editar"
                >
                  <Pencil className="size-4" />
                </Button>
              )}
              {canEdit && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={(e) => { e.stopPropagation(); onDelete(minister); }}
                  title="Eliminar"
                >
                  <Trash2 className="size-4" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [hideDistrictColumn, hideRegionColumn, canEdit, onView, onEdit, onDelete]
  );

  const statusToggle = (
    <div className="flex overflow-hidden rounded-md border text-sm">
      {(["active", "inactive", "all"] as StatusFilter[]).map((s) => (
        <button
          key={s}
          onClick={() => setStatusFilter(s)}
          className={`px-3 py-1.5 transition-colors ${
            statusFilter === s
              ? "bg-primary text-primary-foreground"
              : "hover:bg-muted text-muted-foreground"
          }`}
        >
          {s === "active" ? "Activos" : s === "inactive" ? "Inactivos" : "Todos"}
        </button>
      ))}
    </div>
  );

  return (
    <SelfContainedDataTable
      data={statusFiltered}
      columns={columns}
      isLoading={isLoading}
      getSearchableText={(m) => {
        const adbCode = m.adb_code ?? "";
        return `${adbCode} ${adbCode.replace(/\D/g, "")} ${m.full_name ?? ""} ${m.dni ?? ""}`;
      }}
      searchPlaceholder="Buscar por nombre, código, CI..."
      toolbarActions={statusToggle}
      emptyMessage="No se encontraron ministros"
    />
  );
}
